import { useEffect, useState } from 'react';
import { useSchema } from '../../utils/seo';
import Hero from './components/Hero';
import AppDownload from './components/AppDownload';
import About from './components/About';
import Benefits from './components/Benefits';
import Features from './components/Features';
import Testimonials from './components/Testimonials';
import CTA from './components/CTA';
import Footer from './components/Footer';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Schema.org JSON-LD
  useSchema([
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '온니잡',
      url: import.meta.env.VITE_SITE_URL || 'https://onnijob.com',
      logo: `${import.meta.env.VITE_SITE_URL || 'https://onnijob.com'}/logo.png`,
      description: '퀸알바의 선택은 온니잡에서! 검증된 업체, 안전한 근무환경, 빠른 급여 지급',
      sameAs: [
        'https://play.google.com/store/apps/details?id=kr.onnyjob.app&hl=ko',
        'https://apps.apple.com/kr/app/%EC%98%A8%EB%8B%88%EC%9E%A1-%EC%9D%B5%EB%AA%85-%EC%97%AC%EC%84%B1-%EC%BB%A4%EB%AE%A4%EB%8B%88%ED%8B%B0-%EC%95%8C%EB%B0%94/id6745007770'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        availableLanguage: 'Korean'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: '온니잡',
      image: `${import.meta.env.VITE_SITE_URL || 'https://onnijob.com'}/logo.png`,
      '@id': import.meta.env.VITE_SITE_URL || 'https://onnijob.com',
      url: import.meta.env.VITE_SITE_URL || 'https://onnijob.com',
      telephone: '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '',
        addressLocality: 'Seoul',
        addressRegion: 'Seoul',
        postalCode: '',
        addressCountry: 'KR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 37.5665,
        longitude: 126.9780
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday'
        ],
        opens: '00:00',
        closes: '23:59'
      },
      priceRange: '$$'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '온니잡',
      url: import.meta.env.VITE_SITE_URL || 'https://onnijob.com',
      description: '퀸알바의 선택은 온니잡에서! 검증된 업체, 안전한 근무환경, 빠른 급여 지급',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${import.meta.env.VITE_SITE_URL || 'https://onnijob.com'}/search?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: '온니잡',
      operatingSystem: 'iOS, Android',
      applicationCategory: 'BusinessApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'KRW'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '1250'
      }
    }
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="text-xl sm:text-2xl font-bold text-[#1A2332]">온니잡</div>
          
          {/* 데스크톱 메뉴 */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-[#1A2332] hover:text-[#8B5CF6] transition-colors whitespace-nowrap">
              채용정보
            </a>
            <a href="#benefits" className="text-[#1A2332] hover:text-[#8B5CF6] transition-colors whitespace-nowrap">
              이용방법
            </a>
            <a href="#testimonials" className="text-[#1A2332] hover:text-[#8B5CF6] transition-colors whitespace-nowrap">
              고객후기
            </a>
            <a href="#contact" className="text-[#1A2332] hover:text-[#8B5CF6] transition-colors whitespace-nowrap">
              문의하기
            </a>
            <a 
              href="https://apps.apple.com/kr/app/%EC%98%A8%EB%8B%88%EC%9E%A1-%EC%9D%B5%EB%AA%85-%EC%97%AC%EC%84%B1-%EC%BB%A4%EB%AE%A4%EB%8B%88%ED%8B%B0-%EC%95%8C%EB%B0%94/id6745007770" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#8B5CF6] text-white px-6 py-2.5 rounded-full hover:bg-[#7C3AED] transition-colors whitespace-nowrap"
            >
              지금 시작하기
            </a>
          </div>

          {/* 모바일 햄버거 버튼 */}
          <button 
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="메뉴"
          >
            <div className="relative w-6 h-5">
              <span className={`absolute left-0 w-6 h-0.5 bg-[#1A2332] transition-all duration-300 ${menuOpen ? 'top-2 rotate-45' : 'top-0'}`}></span>
              <span className={`absolute left-0 top-2 w-6 h-0.5 bg-[#1A2332] transition-all duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`absolute left-0 w-6 h-0.5 bg-[#1A2332] transition-all duration-300 ${menuOpen ? 'top-2 -rotate-45' : 'top-4'}`}></span>
            </div>
          </button>
        </div>

        {/* 모바일 메뉴 */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-80' : 'max-h-0'}`}>
          <div className="px-4 pb-6 space-y-4">
            <a href="#about" onClick={() => setMenuOpen(false)} className="block text-[#1A2332] hover:text-[#8B5CF6] transition-colors py-2">
              채용정보
            </a>
            <a href="#benefits" onClick={() => setMenuOpen(false)} className="block text-[#1A2332] hover:text-[#8B5CF6] transition-colors py-2">
              이용방법
            </a>
            <a href="#testimonials" onClick={() => setMenuOpen(false)} className="block text-[#1A2332] hover:text-[#8B5CF6] transition-colors py-2">
              고객후기
            </a>
            <a href="#contact" onClick={() => setMenuOpen(false)} className="block text-[#1A2332] hover:text-[#8B5CF6] transition-colors py-2">
              문의하기
            </a>
            <a 
              href="https://apps.apple.com/kr/app/%EC%98%A8%EB%8B%88%EC%9E%A1-%EC%9D%B5%EB%AA%85-%EC%97%AC%EC%84%B1-%EC%BB%A4%EB%AE%A4%EB%8B%88%ED%8B%B0-%EC%95%8C%EB%B0%94/id6745007770" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block bg-[#8B5CF6] text-white px-6 py-3 rounded-full hover:bg-[#7C3AED] transition-colors text-center whitespace-nowrap"
            >
              지금 시작하기
            </a>
          </div>
        </div>
      </nav>

      <Hero />
      <AppDownload />
      <About />
      <Benefits />
      <Features />
      <Testimonials />
      <CTA />
      <Footer />

      {/* 카카오톡 플로팅 버튼 */}
      <a
        href="http://pf.kakao.com/_xgsPfn/friend"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-[#FEE500] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 cursor-pointer"
        aria-label="카카오톡 상담"
      >
        <svg width="24" height="24" className="sm:w-7 sm:h-7" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M128 36C70.562 36 24 74.238 24 121.5C24 152.078 44.818 178.762 76.062 193.312L66.312 228.562C65.688 230.812 68.312 232.688 70.312 231.438L113.188 203.688C117.938 204.188 122.938 204.5 128 204.5C185.438 204.5 232 166.262 232 119C232 74.238 185.438 36 128 36Z" fill="#3C1E1E"/>
        </svg>
      </a>
    </div>
  );
}
